import EmailLink from "@/components/atoms/EmailLink";
import ErrorComponent from "@/components/atoms/ErrorComponent";
import Flex from "@/components/atoms/Flex";
import IconButton from "@/components/atoms/IconButton";
import Loader from "@/components/atoms/Loader";
import PhoneCallLink from "@/components/atoms/PhoneCallLink";
import SoftBadge from "@/components/atoms/SoftBadge";
import { getColorFromRole, getReelRoleName } from "@/helpers/UserHelpers";
import { PaginateCardProps } from "@/interfaces/Card";
import { CompanyUserFilters } from "@/interfaces/CompanyUser";
import {
  useDeleteUserCompanyMutation,
  useGetUserCompaniesQuery,
} from "@/services/users/companies";
import {
  faAt,
  faClock,
  faEllipsisVertical,
  faPhone,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { DateTime } from "luxon";
import { ElementRef, FC, Fragment, useEffect, useRef, useState } from "react";
import { Col, Dropdown, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ToastContentProps, toast } from "react-toastify";
import AreYouSurToContinueModal from "../../modals/AreYouSurToContinueModal";
import UnDispatchCompaniesModal from "../../modals/user/UnDispatchCompaniesModal";
import PaginateCard from "../PaginateCard";

const UserCompaniesCard: FC<
  PaginateCardProps & CompanyUserFilters & { userId: string }
> = ({ onGetTotal, showDelete = true, userId, ...props }) => {
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const UnDispatchModalRef =
    useRef<ElementRef<typeof UnDispatchCompaniesModal>>(null);
  const [filters, setFilters] = useState<CompanyUserFilters>({
    pageIndex: 0,
    pageSize: 10,
    ...props,
  });

  const { data, isLoading, isError, error } = useGetUserCompaniesQuery([
    userId,
    filters,
  ]);

  const [deleteUserCompanyAsync] = useDeleteUserCompanyMutation();

  const onDelete = async (id: string) => {
    if (id) {
      toast.promise(deleteUserCompanyAsync([userId, id]).unwrap(), {
        pending: "Détachement de l'entreprise en cours ...",
        success: {
          render() {
            DeleteModalRef.current?.showOrHiddenModal();
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                L'entreprise à bien été détachée 🤩
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }
            return "Une erreur est survenue";
          },
        },
      });
    }
  };

  useEffect(() => {
    data?.meta && onGetTotal?.(data.meta.total);
  }, [data?.meta]);

  if (isLoading) {
    return <Loader />;
  } else if (isError) {
    return <ErrorComponent error={error} />;
  }

  return (
    <Fragment>
      <UnDispatchCompaniesModal ref={UnDispatchModalRef} />
      <PaginateCard
        meta={data?.meta}
        filters={filters}
        setFilters={setFilters}
        headerActions={
          <IconButton
            variant="nolina-default"
            size="sm"
            icon={faTrash}
            transform="shrink-3"
            className="mx-2"
            iconAlign="middle"
            onClick={() =>
              UnDispatchModalRef.current?.showOrHiddenModal(userId)
            }
          >
            <span className="d-none d-sm-inline-block d-xl-none d-xxl-inline-block ms-1">
              Détacher par statut
            </span>
          </IconButton>
        }
      >
        <AreYouSurToContinueModal
          ref={DeleteModalRef}
          onContinue={onDelete}
          title="Êtes vous sûr de vouloir détacher l'entreprise qui à pour id : {{id}}"
        />
        {data?.data.map(
          ({
            id,
            company: { name, createdAt, email, phone, status },
            role,
            companyId,
          }) => (
            <div
              key={id}
              className="bg-white dark__bg-1100 p-x1 rounded-3 shadow-sm"
            >
              <Row className="flex-between-center">
                <Col xs={10}>
                  <p className="fw-semi-bold mb-0">
                    <Link to={`/companies/${companyId}`}>{name}</Link>
                  </p>
                </Col>
                {showDelete && (
                  <Col xs={"auto"} className="d-flex justify-content-end">
                    <Dropdown
                      align="end"
                      className="btn-reveal-trigger d-inline-block"
                    >
                      <Dropdown.Toggle split variant="nolina-default" size="sm">
                        <FontAwesomeIcon
                          icon={faEllipsisVertical}
                          className="fs--2"
                        />
                      </Dropdown.Toggle>

                      <Dropdown.Menu className="border py-0">
                        <div className="py-2">
                          <Dropdown.Item
                            className="text-danger"
                            onClick={() =>
                              DeleteModalRef.current?.showOrHiddenModal(
                                companyId
                              )
                            }
                          >
                            Supprimer
                          </Dropdown.Item>
                        </div>
                      </Dropdown.Menu>
                    </Dropdown>
                  </Col>
                )}
              </Row>
              <div className="border-bottom border-lg-0 border-0 my-3"></div>
              <Row className="flex-between-center">
                <Col xs={12} lg={8}>
                  <div className="d-flex gap-2 flex-wrap mb-2">
                    <SoftBadge bg={getColorFromRole(role as any)}>
                      {getReelRoleName(role as any)}
                    </SoftBadge>
                    {status && (
                      <SoftBadge
                        bg={status.backgroundColor}
                        textColor={status.textColor}
                      >
                        {status.name}
                      </SoftBadge>
                    )}
                  </div>
                  {email && <EmailLink email={email} className="h6" />}
                  {phone && (
                    <PhoneCallLink
                      number={phone}
                      className="d-flex align-items-center gap-1 h6 mb-0 text-600"
                    />
                  )}
                </Col>
                <Col xs={12} lg={"auto"}>
                  <div className="border-bottom border-lg-0 border-0 my-lg-0 my-3"></div>
                  <Flex direction="column">
                    <h6 className="d-flex align-items-center gap-1 mb-0 mt-2">
                      <FontAwesomeIcon
                        icon={faClock}
                        transform="shrink-3 up-1"
                      />
                      <span className="text-600">
                        {DateTime.fromISO(createdAt)
                          .setLocale("fr")
                          .toFormat("dd LLL yyyy")}
                      </span>
                    </h6>
                  </Flex>
                </Col>
              </Row>
            </div>
          )
        )}
      </PaginateCard>
    </Fragment>
  );
};

export default UserCompaniesCard;
